import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AWSS3Service,
  CreateUserRequest,
  KeycloakRealm,
  UpdateUsernameRequest,
  UpdateUserNicknameRequest,
  UserRecord,
  UserService,
} from '@libs/shared/api';
import { BasicSnackbarService } from '@libs/shared/ui/basic-snackbar/basic-snackbar.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthUserProfile } from '@sprint-expert/app/core/auth/auth-user-profile';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import { mergeMap, pipe, Subject, switchMap } from 'rxjs';

interface UserCoreState {
  content: UserRecord;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
}

const initialState: UserCoreState = {
  content: undefined,
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
};

@Injectable({
  providedIn: 'root',
})
export class CurrentUserCoreStore {
  readonly #userService = inject(UserService);
  readonly #basicSnackbarService = inject(BasicSnackbarService);
  readonly #s3Service = inject(AWSS3Service);
  readonly #http = inject(HttpClient);

  readonly state$$ = signalState(initialState);
  readonly content$$ = this.state$$.content;
  readonly isLoading$$ = this.state$$.isLoading;
  readonly isLoaded$$ = this.state$$.isLoaded;
  readonly errorMessage$$ = this.state$$.errorMessage;

  constructor() {}

  readonly created$ = new Subject<UserRecord>();
  readonly updated$ = new Subject<UserRecord>();

  createUserByAuthUserProfile = rxMethod<{
    authUserProfile: AuthUserProfile;
    realm: KeycloakRealm;
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.fetchUserById(payload.authUserProfile.info.sub).pipe(
          tapResponse(
            (user: UserRecord) => {
              patchState(this.state$$, {
                content: user,
                isLoading: false,
                isLoaded: true,
                errorMessage: '',
              });
            },
            (e: HttpErrorResponse) => {
              if (e.status === 404) {
                this.createUser({
                  request: {
                    id: payload.authUserProfile.info.sub,
                    firstName: payload.authUserProfile.info.given_name ?? '',
                    lastName: payload.authUserProfile.info.family_name ?? '',
                    emailAddress: payload.authUserProfile.info.email,
                    timezone:
                      typeof Intl === 'object' &&
                      Intl.DateTimeFormat &&
                      Intl.DateTimeFormat().resolvedOptions
                        ? Intl.DateTimeFormat().resolvedOptions().timeZone
                        : 'Asia/Seoul',
                    languageCode: 'ko',
                    googleUser: false,
                    appleUser: false,
                    realm: payload.realm,
                  },
                  showSuccessSnackbar: false,
                  showFailureSnackbar: false,
                });
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  fetchUserById = rxMethod<{
    id: string;
    eventType?: 'created' | 'updated';
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.fetchUserById(payload.id).pipe(
          tapResponse(
            (user: UserRecord) => {
              patchState(this.state$$, {
                content: user,
                isLoading: false,
                isLoaded: true,
                errorMessage: '',
              });

              switch (payload.eventType) {
                case 'created':
                  this.created$.next(user);
                  break;
                case 'updated':
                  this.updated$.next(user);
                  break;
              }
            },
            (e: HttpErrorResponse) => {
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  createUser = rxMethod<{
    request: CreateUserRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.createUser(payload.request).pipe(
          tapResponse(
            (userId: string) => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('유저가 생성 되었어요.');
              }
              this.fetchUserById({ id: userId, eventType: 'created' });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open('유저 생성에 실패했어요.');
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateUsername = rxMethod<{
    id: string;
    request: UpdateUsernameRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.updateUsername(payload.id, payload.request).pipe(
          tapResponse(
            () => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('유저 이름이 변경되었어요.');
              }
              this.fetchUserById({ id: payload.id, eventType: 'updated' });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open('유저 이름 변경에 실패했어요.');
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateUserNickname = rxMethod<{
    id: string;
    request: UpdateUserNicknameRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.updateUserNickname(payload.id, payload.request).pipe(
          tapResponse(
            () => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('닉네임이 변경되었어요.');
              }
              this.fetchUserById({ id: payload.id, eventType: 'updated' });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open('닉네임 변경에 실패했어요.');
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  syncUserRole = rxMethod<string>(
    pipe(
      switchMap(userId =>
        this.#userService.syncUserRole(userId).pipe(
          tapResponse(
            () => {
              this.fetchUserById({ id: userId, eventType: 'updated' });
            },
            (e: HttpErrorResponse) => {
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateUserProfileImage = rxMethod<{
    id: string;
    imageFile: Blob;
    contentType: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#s3Service.getPresignedImagePut().pipe(
          mergeMap(presignedPutUrl =>
            this.#http
              .put(presignedPutUrl.url, payload.imageFile, {
                headers: { 'Content-Type': payload.contentType },
              })
              .pipe(
                mergeMap(() =>
                  this.#userService
                    .updateUserProfileImage(payload.id, {
                      profileImageFileKey: presignedPutUrl.fileKey,
                    })
                    .pipe(
                      tapResponse(
                        () => {
                          if (payload.showSuccessSnackbar) {
                            this.#basicSnackbarService.open(
                              '프로필 이미지가 변경되었어요! 🚀'
                            );
                          }
                          this.fetchUserById({
                            id: payload.id,
                            eventType: 'updated',
                          });
                        },
                        (e: HttpErrorResponse) => {
                          if (payload.showFailureSnackbar) {
                            this.#basicSnackbarService.open(
                              '프로필 이미지 변경에 실패했어요. 😥'
                            );
                          }
                          patchState(this.state$$, {
                            content: undefined,
                            isLoading: false,
                            isLoaded: false,
                            errorMessage: extractErrorMessage(e),
                          });
                        }
                      )
                    )
                )
              )
          )
        )
      )
    )
  );

  deleteUserProfileImage = rxMethod<{
    id: string;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#userService.deleteUserProfileImage(payload.id).pipe(
          tapResponse(
            () => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open(
                  '프로필 이미지가 삭제되었어요.'
                );
              }
              this.fetchUserById({ id: payload.id, eventType: 'updated' });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open(
                  '프로필 이미지 삭제에 실패했어요.'
                );
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );
}
